// Browser API client. Talks to services/api over the same-origin `/api` proxy
// (Vite dev) or via nginx (prod).
//
// Auth:
//   - entra mode → session cookie (httpOnly) sent automatically via
//     `credentials: "include"`. No tokens in JS (Option A BFF).
//   - mock  mode → X-Mock-User-* headers from the stored dev session.

import { authMode, getMockSession } from "./auth";

class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function buildAuthHeaders(): Record<string, string> {
  if (authMode() === "mock") {
    const session = getMockSession();
    return {
      "X-Mock-User-Email": session?.email ?? "dev@albertvdscheur.nl",
      "X-Mock-User-Roles": (session?.roles ?? ["admin"]).join(","),
    };
  }
  // entra mode: the httpOnly session cookie travels with credentials:"include".
  return {};
}

async function request<T>(
  method: string,
  path: string,
  options: { query?: Record<string, string | undefined>; body?: unknown } = {},
): Promise<T> {
  const url = new URL(path, window.location.origin);
  if (options.query) {
    for (const [k, v] of Object.entries(options.query)) {
      if (v !== undefined) url.searchParams.set(k, v);
    }
  }

  const init: RequestInit = {
    method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  const res = await fetch(url.toString(), init);

  if (!res.ok) {
    let code = "API_ERROR";
    let message = `API request failed with status ${res.status}`;
    try {
      const json = (await res.json()) as {
        error?: { code?: string; message?: string };
      };
      if (json.error?.code) code = json.error.code;
      if (json.error?.message) message = json.error.message;
    } catch {
      // non-JSON error body — keep defaults
    }
    throw new ApiError(res.status, code, message);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// Binary GET (e.g. a rendered PDF) with the same auth as `request`. Returns the
// response Blob so the caller can open or download it.
async function requestBlob(path: string): Promise<Blob> {
  const url = new URL(path, window.location.origin);
  const res = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
    headers: { ...buildAuthHeaders() },
  });
  if (!res.ok) {
    let code = "API_ERROR";
    let message = `API request failed with status ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { code?: string; message?: string } };
      if (json.error?.code) code = json.error.code;
      if (json.error?.message) message = json.error.message;
    } catch {
      // non-JSON error body — keep defaults
    }
    throw new ApiError(res.status, code, message);
  }
  return res.blob();
}

export const apiClient = {
  get<T>(path: string, query?: Record<string, string | undefined>): Promise<T> {
    return request<T>("GET", path, { query });
  },
  getBlob(path: string): Promise<Blob> {
    return requestBlob(path);
  },
  post<T>(path: string, body: unknown): Promise<T> {
    return request<T>("POST", path, { body });
  },
  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>("PATCH", path, { body });
  },
  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>("PUT", path, { body });
  },
  delete<T>(path: string): Promise<T> {
    return request<T>("DELETE", path);
  },
};

export { ApiError };
